import { Grid } from '@mui/material';
import {
  dateFormatShortTime,
  getDiffTimeText,
  parseHMtoSeconds,
  parseSecondsToHM,
} from '@nx-smartmonkey/shared/helpers';
import { HSpacer, RoutalPalette } from '@nx-smartmonkey/ui';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { HexColor } from '@nx-smartmonkey/shared/domain';
import { RouteAvatarName } from '../../../../components/RouteAvatarName';
import { RightPanelHeader } from '../../../../components/right-panel-info/RightPanel.styled';
import DRIVER_STATUS from '../../../../constants/Drivers';
import * as routing from '../../../../constants/Routing';
import { Driver as DriverDomain } from '../../../../domain/driver/Driver';
import { Project } from '../../../../domain/project/Project';
import Text from '../../../../react-components/Text';

import { getRouteColor } from '../../../../constants/Colors';
import { DriverStatusBar } from '../components/StatusBar';
import { useUpdateDriver } from '../hooks/driver/useUpdateDriver';
import { useRetrieveActivePlanDrivers } from '../hooks/plan-details/useRetrieveActivePlanDrivers';

const DriverTimeDiff = ({ plannedTimeSeconds }: { plannedTimeSeconds: number }) => {
  const realDateHM = dateFormatShortTime(new Date());
  const realDateSeconds = parseHMtoSeconds(realDateHM);
  if (realDateSeconds > plannedTimeSeconds) {
    const diff = getDiffTimeText(realDateSeconds - plannedTimeSeconds);
    return (
      <Grid container direction="row" alignItems="center" style={{ width: `unset` }}>
        <span style={{ color: `${RoutalPalette.secondary00}`, fontSize: `10px` }}>{`+${diff}`}</span>
        <Text
          tagName="span"
          intlId="late"
          style={{
            marginLeft: `4px`,
            color: `${RoutalPalette.secondary00}`,
            fontSize: `10px`,
            textTransform: `lowercase`,
          }}
        />
      </Grid>
    );
  }
  return null;
};

type DriverHeaderProps = {
  driver: DriverDomain;
  onClose: (...args: any[]) => any;
  warning?: React.ReactElement;
  options?: React.ReactElement;
  activeProject?: Project;
};

export const DriverHeader = ({ driver, onClose, warning, options, activeProject }: DriverHeaderProps) => {
  const navigate = useNavigate();
  const { run: updateDriver } = useUpdateDriver();
  const { data: planDrivers } = useRetrieveActivePlanDrivers();

  const handleUpdate = (label: string, val: any) => {
    updateDriver({ driver, changes: { [label]: val } });
  };

  if (driver) {
    return (
      <RightPanelHeader onClose={onClose} style={{ padding: `0 10px 0 4px` }}>
        <Grid container direction="row" wrap="nowrap" alignItems="center">
          <Grid item style={{ height: `55px` }}>
            <DriverStatusBar status={driver.status} />
          </Grid>

          <HSpacer small />

          <Grid item style={{ height: `55px`, display: `flex`, alignItems: `center` }}>
            <RouteAvatarName
              name={driver.getShortLabel()}
              color={planDrivers ? getRouteColor(driver, planDrivers) : driver.color?.value}
              withColorPicker
              onUpdate={(color: HexColor) => {
                handleUpdate(`color`, color);
              }}
            />
          </Grid>
          <Grid item container justifyContent="flex-start" style={{ width: `100%` }} wrap="nowrap" alignItems="center">
            <HSpacer small />

            <Grid container direction="column">
              {driver.planned_start_time || driver.planned_start_time === 0 ? (
                <Grid container direction="row" wrap="nowrap">
                  <Grid item>
                    <span style={{ color: `${RoutalPalette.text}`, fontSize: `11px` }}>
                      {`${parseSecondsToHM(driver.planned_start_time)} - ${parseSecondsToHM(driver.planned_end_time)}`}
                    </span>
                  </Grid>

                  {driver.status === DRIVER_STATUS.inTransit && driver.planned_start_time ? (
                    <>
                      <HSpacer medium />

                      <DriverTimeDiff plannedTimeSeconds={driver.planned_start_time} />
                    </>
                  ) : null}
                </Grid>
              ) : (
                <Grid container direction="row" wrap="nowrap">
                  <Grid item>
                    <Text
                      tagName="span"
                      intlId="properties.route.no_assigned_route"
                      style={{
                        color: `${RoutalPalette.secondary00}`,
                        fontSize: `10px`,
                      }}
                    />
                  </Grid>
                </Grid>
              )}

              {driver.vehicle_id ? (
                <Grid item container>
                  <Text
                    intlId="properties.route.view_in_vehicles"
                    variant="button"
                    style={{ fontSize: `11px`, marginBottom: `8px` }}
                    onClick={() => {
                      navigate(
                        `${routing.DRIVERS.VEHICLES.replace(
                          `:projectId`,
                          activeProject?.id ?? ``
                        )}?detailType=vehicle&detailId=${driver.vehicle_id}`
                      );
                    }}
                  />
                </Grid>
              ) : null}
            </Grid>
          </Grid>
          <Grid container direction="row" style={{ width: `auto` }} wrap="nowrap" alignItems="center">
            {warning}
            <HSpacer medium />
            {options}
          </Grid>
        </Grid>
      </RightPanelHeader>
    );
  }

  return null;
};
